/**
 * Analytics Utility
 * Handles Google Analytics 4 and custom PWA analytics tracking
 */

// Type definitions for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Get GA4 Measurement ID from environment
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// ============================================
// Google Analytics 4 Integration
// ============================================

/**
 * Initialize Google Analytics 4
 * Should be called once on app startup
 */
export function initializeGA(): void {
  if (!GA_MEASUREMENT_ID) {
    console.debug('[Analytics] GA4 Measurement ID not configured');
    return;
  }

  // Dynamically load the GA4 script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: any[]) {
    window.dataLayer!.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll track page views manually for SPA
    cookie_flags: 'SameSite=None;Secure',
  });

  console.debug('[Analytics] GA4 initialized with ID:', GA_MEASUREMENT_ID);
}

/**
 * Track page view (for SPA navigation)
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });

  console.debug('[Analytics] Page view:', pagePath);
}

/**
 * Track custom events
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, any>
): void {
  if (!window.gtag) return;

  window.gtag('event', eventName, eventParams);
  console.debug('[Analytics] Event:', eventName, eventParams);
}

/**
 * Set user properties (for authenticated users)
 */
export function setUserProperties(userId: string, properties?: Record<string, any>): void {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId,
    ...properties,
  });

  console.debug('[Analytics] User properties set for:', userId);
}

// ============================================
// PWA-Specific Analytics
// ============================================

interface PWAMetrics {
  installPromptShown: number;
  installAccepted: number;
  installDismissed: number;
  offlinePagesViewed: number;
  backgroundSyncSuccess: number;
  backgroundSyncFailure: number;
  cacheHits: number;
  cacheMisses: number;
  serviceWorkerErrors: number;
  pushNotificationsEnabled: number;
  pushNotificationsClicked: number;
}

const PWA_METRICS_KEY = 'checksandticks_pwa_metrics';

/**
 * Get current PWA metrics from localStorage
 */
function getPWAMetrics(): PWAMetrics {
  try {
    const stored = localStorage.getItem(PWA_METRICS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('[PWA Analytics] Failed to parse stored metrics');
  }

  return {
    installPromptShown: 0,
    installAccepted: 0,
    installDismissed: 0,
    offlinePagesViewed: 0,
    backgroundSyncSuccess: 0,
    backgroundSyncFailure: 0,
    cacheHits: 0,
    cacheMisses: 0,
    serviceWorkerErrors: 0,
    pushNotificationsEnabled: 0,
    pushNotificationsClicked: 0,
  };
}

/**
 * Save PWA metrics to localStorage
 */
function savePWAMetrics(metrics: PWAMetrics): void {
  try {
    localStorage.setItem(PWA_METRICS_KEY, JSON.stringify(metrics));
  } catch (error) {
    console.warn('[PWA Analytics] Failed to save metrics');
  }
}

/**
 * Increment a PWA metric counter
 */
function incrementPWAMetric(metricName: keyof PWAMetrics): void {
  const metrics = getPWAMetrics();
  metrics[metricName]++;
  savePWAMetrics(metrics);

  // Also track in GA4 if available
  trackEvent('pwa_metric', {
    metric_name: metricName,
    metric_value: metrics[metricName],
  });
}

// ============================================
// PWA Event Trackers
// ============================================

/**
 * Track PWA install prompt shown
 */
export function trackInstallPromptShown(): void {
  incrementPWAMetric('installPromptShown');
  trackEvent('pwa_install_prompt_shown');
}

/**
 * Track PWA install accepted
 */
export function trackInstallAccepted(): void {
  incrementPWAMetric('installAccepted');
  trackEvent('pwa_install_accepted');
}

/**
 * Track PWA install dismissed
 */
export function trackInstallDismissed(): void {
  incrementPWAMetric('installDismissed');
  trackEvent('pwa_install_dismissed');
}

/**
 * Track offline page view
 */
export function trackOfflinePageView(pagePath: string): void {
  incrementPWAMetric('offlinePagesViewed');
  trackEvent('pwa_offline_page_view', { page_path: pagePath });
}

/**
 * Track background sync success
 */
export function trackBackgroundSyncSuccess(itemCount: number): void {
  incrementPWAMetric('backgroundSyncSuccess');
  trackEvent('pwa_background_sync_success', { item_count: itemCount });
}

/**
 * Track background sync failure
 */
export function trackBackgroundSyncFailure(error: string): void {
  incrementPWAMetric('backgroundSyncFailure');
  trackEvent('pwa_background_sync_failure', { error_message: error });
}

/**
 * Track cache hit
 */
export function trackCacheHit(resourceType: string): void {
  incrementPWAMetric('cacheHits');
  // Don't track individual cache hits in GA4 to avoid quota issues
}

/**
 * Track cache miss
 */
export function trackCacheMiss(resourceType: string): void {
  incrementPWAMetric('cacheMisses');
  // Don't track individual cache misses in GA4 to avoid quota issues
}

/**
 * Track service worker error
 */
export function trackServiceWorkerError(errorMessage: string): void {
  incrementPWAMetric('serviceWorkerErrors');
  trackEvent('pwa_service_worker_error', { error_message: errorMessage });
}

/**
 * Track push notification enabled
 */
export function trackPushNotificationEnabled(): void {
  incrementPWAMetric('pushNotificationsEnabled');
  trackEvent('pwa_push_notification_enabled');
}

/**
 * Track push notification clicked
 */
export function trackPushNotificationClicked(notificationType: string): void {
  incrementPWAMetric('pushNotificationsClicked');
  trackEvent('pwa_push_notification_clicked', { notification_type: notificationType });
}

/**
 * Get PWA install rate
 */
export function getPWAInstallRate(): number {
  const metrics = getPWAMetrics();
  if (metrics.installPromptShown === 0) return 0;
  return (metrics.installAccepted / metrics.installPromptShown) * 100;
}

/**
 * Get cache hit rate
 */
export function getCacheHitRate(): number {
  const metrics = getPWAMetrics();
  const total = metrics.cacheHits + metrics.cacheMisses;
  if (total === 0) return 0;
  return (metrics.cacheHits / total) * 100;
}

/**
 * Get background sync success rate
 */
export function getBackgroundSyncSuccessRate(): number {
  const metrics = getPWAMetrics();
  const total = metrics.backgroundSyncSuccess + metrics.backgroundSyncFailure;
  if (total === 0) return 0;
  return (metrics.backgroundSyncSuccess / total) * 100;
}

/**
 * Get all PWA metrics for display
 */
export function getAllPWAMetrics(): PWAMetrics & {
  installRate: number;
  cacheHitRate: number;
  syncSuccessRate: number;
} {
  const metrics = getPWAMetrics();
  return {
    ...metrics,
    installRate: getPWAInstallRate(),
    cacheHitRate: getCacheHitRate(),
    syncSuccessRate: getBackgroundSyncSuccessRate(),
  };
}

// ============================================
// Business Event Trackers
// ============================================

/**
 * Track user authentication events
 */
export function trackAuth(action: 'login' | 'logout' | 'register' | 'password_reset'): void {
  trackEvent(`auth_${action}`);
}

/**
 * Track form submission
 */
export function trackFormSubmission(formId: string, formName: string, submissionType: string): void {
  trackEvent('form_submission', {
    form_id: formId,
    form_name: formName,
    submission_type: submissionType,
  });
}

/**
 * Track asset scan
 */
export function trackAssetScan(assetId: string, scanMethod: 'qr' | 'nfc' | 'manual'): void {
  trackEvent('asset_scan', {
    asset_id: assetId,
    scan_method: scanMethod,
  });
}

/**
 * Track feature usage
 */
export function trackFeatureUsage(featureName: string, action: string): void {
  trackEvent('feature_usage', {
    feature_name: featureName,
    action: action,
  });
}

/**
 * Track errors for monitoring
 */
export function trackError(errorType: string, errorMessage: string, errorStack?: string): void {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    error_stack: errorStack?.substring(0, 500), // Truncate to avoid quota issues
  });
}

// ============================================
// Network Status Analytics
// ============================================

let isOnline = navigator.onLine;

/**
 * Initialize network status tracking
 */
export function initializeNetworkTracking(): void {
  window.addEventListener('online', () => {
    if (!isOnline) {
      trackEvent('network_status_change', { status: 'online' });
      isOnline = true;
    }
  });

  window.addEventListener('offline', () => {
    if (isOnline) {
      trackEvent('network_status_change', { status: 'offline' });
      isOnline = false;
    }
  });

  console.debug('[Analytics] Network tracking initialized');
}

// ============================================
// Export all functions
// ============================================

export default {
  // GA4
  initializeGA,
  trackPageView,
  trackEvent,
  setUserProperties,

  // PWA
  trackInstallPromptShown,
  trackInstallAccepted,
  trackInstallDismissed,
  trackOfflinePageView,
  trackBackgroundSyncSuccess,
  trackBackgroundSyncFailure,
  trackCacheHit,
  trackCacheMiss,
  trackServiceWorkerError,
  trackPushNotificationEnabled,
  trackPushNotificationClicked,
  getPWAInstallRate,
  getCacheHitRate,
  getBackgroundSyncSuccessRate,
  getAllPWAMetrics,

  // Business
  trackAuth,
  trackFormSubmission,
  trackAssetScan,
  trackFeatureUsage,
  trackError,

  // Network
  initializeNetworkTracking,
};
